import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { storeToken, storeUser } from "../../reducers/app.reducer";
import signupProcess from "./signup.component.service";

const SignupComponent = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Email is invalid";

    if (!formData.mobile) newErrors.mobile = "Mobile number is required";
    else if (!/^[0-9]{10}$/.test(formData.mobile))
      newErrors.mobile = "Invalid mobile number";

    if (!formData.password) newErrors.password = "Password is required";
    else if (formData.password.length < 6)
      newErrors.password = "Password must be at least 6 characters";

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const response = await signupProcess({
        customer_name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        password: formData.password,
      });

      if (response.status) {
        // Auto-login after successful signup
        dispatch(
          storeToken({
            token: response.token,
            user: response.user || null,
          })
        );
        dispatch(
          storeUser({
            user: response.user || null,
          })
        );
        navigate("/login");
      } else {
        alert(response.message || "Signup failed. Please try again.");
      }
    } catch (error) {
      console.error("Signup error:", error);
      alert("An error occurred during signup. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="ds-auth">
      <aside className="ds-auth-aside">
        <div className="inner">
          <img
            src="/assets/img/semi360-logo.svg"
            alt="semi 360"
            style={{ height: 30, filter: "brightness(0) invert(1)", marginBottom: 40 }}
          />
          <h2>Create your<br />sourcing account</h2>
          <p>
            Free to join. Get instant access to live pricing, BOM matching,
            quotes and order tracking.
          </p>
        </div>
        <img className="glyph" src="/assets/img/logo-360-mark.svg" alt="" />
      </aside>

      <main className="ds-auth-main">
        <form onSubmit={handleSubmit} className="ds-auth-card">
          <p className="ds-eyebrow" style={{ margin: 0 }}>Get started</p>
          <h1>Create your account</h1>
          <p className="lede">It only takes a minute.</p>

          <div className="ds-field">
            <label htmlFor="name">Full Name</label>
            <div className="ds-input">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
              />
            </div>
            {errors.name && <span style={{ color: "var(--danger)", fontSize: 12 }}>{errors.name}</span>}
          </div>

          <div className="ds-field">
            <label htmlFor="email">Email</label>
            <div className="ds-input">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
              />
            </div>
            {errors.email && <span style={{ color: "var(--danger)", fontSize: 12 }}>{errors.email}</span>}
          </div>

          <div className="ds-field">
            <label htmlFor="mobile">Mobile Number</label>
            <div className="ds-input">
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="Enter your mobile number"
                required
              />
            </div>
            {errors.mobile && <span style={{ color: "var(--danger)", fontSize: 12 }}>{errors.mobile}</span>}
          </div>

          <div className="ds-field">
            <label htmlFor="password">Password</label>
            <div className="ds-input">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a password"
                required
              />
              <button
                type="button"
                className="trail"
                onClick={() => setShowPassword(!showPassword)}
                aria-label="Toggle password"
              >
                <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
              </button>
            </div>
            {errors.password && <span style={{ color: "var(--danger)", fontSize: 12 }}>{errors.password}</span>}
          </div>

          <div className="ds-field">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="ds-input">
              <input
                type={showPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                required
              />
            </div>
            {errors.confirmPassword && <span style={{ color: "var(--danger)", fontSize: 12 }}>{errors.confirmPassword}</span>}
          </div>

          <button
            type="submit"
            className="ds-btn ds-btn-primary ds-btn-block ds-btn-lg"
            disabled={isLoading}
          >
            {isLoading ? "Creating account…" : "Create account"}
          </button>

          <p style={{ textAlign: "center", color: "var(--fg-3)", margin: 0 }}>
            Already have an account? <Link className="ds-link" to="/login">Sign in</Link>
          </p>
        </form>
      </main>
    </div>
  );
};

export default SignupComponent;
